import React from 'react';
import { AnimationConfig } from '../../types/animated';
import { interpolate, Easing } from 'remotion';

/**
 * Fade animation utility functions for AnimatedItem interface
 * Used by AnimatedItemImpl.getAnimationTransform() method
 * Pure functions that calculate animation values
 */

/**
 * Calculate fade animation opacity value
 * @param progress - Animation progress (0 to 1)
 * @param direction - Animation direction ('entrance' or 'exit')
 * @returns Opacity value for current animation frame
 */
export function calculateFadeOpacity(progress: number, direction: 'entrance' | 'exit'): number {
  // Validate input
  if (progress < 0 || progress > 1) {
    console.warn(`Invalid progress value: ${progress}. Must be between 0 and 1.`);
    progress = Math.max(0, Math.min(1, progress));
  }

  // Use Remotion's interpolate with subtle easing for smoother fades
  if (direction === 'entrance') {
    return interpolate(progress, [0, 1], [0, 1], {
      easing: Easing.out(Easing.ease)
    });
  } else {
    return interpolate(progress, [0, 1], [1, 0], {
      easing: Easing.in(Easing.ease)
    });
  }
}

/**
 * Get CSS transform for fade animation with hardware acceleration
 * @param opacity - Current opacity value
 * @returns CSS style object for fade animation
 */
export function getFadeAnimationStyle(opacity: number): React.CSSProperties {
  return {
    opacity,
    willChange: 'opacity',
    transform: 'translateZ(0)', // Hardware acceleration
  };
}

/**
 * Calculate fadeOutUp animation (fade out while moving upward)
 * @param progress - Animation progress (0 to 1)
 * @param direction - Animation direction ('entrance' or 'exit')
 * @returns CSS style object for fadeOutUp animation
 */
export function calculateFadeOutUpAnimation(progress: number, direction: 'entrance' | 'exit'): React.CSSProperties {
  // Validate input
  if (progress < 0 || progress > 1) {
    console.warn(`Invalid progress value: ${progress}. Must be between 0 and 1.`);
    progress = Math.max(0, Math.min(1, progress));
  }

  // For exit direction, fade out and move up
  if (direction === 'exit') {
    const opacity = interpolate(progress, [0, 1], [1, 0], {
      easing: Easing.in(Easing.ease)
    });
    const translateY = interpolate(progress, [0, 1], [0, -30], {
      easing: Easing.in(Easing.ease)
    });
    
    return {
      opacity,
      transform: `translateY(${translateY}px) translateZ(0)`,
      willChange: 'opacity, transform'
    };
  } else {
    // For entrance, reverse the animation (move down and fade in)
    const opacity = interpolate(progress, [0, 1], [0, 1], {
      easing: Easing.out(Easing.ease)
    });
    const translateY = interpolate(progress, [0, 1], [-30, 0], {
      easing: Easing.out(Easing.ease)
    });
    
    return {
      opacity,
      transform: `translateY(${translateY}px) translateZ(0)`,
      willChange: 'opacity, transform'
    };
  }
}

/**
 * Calculate fadeInDown animation (fade in while moving downward)
 * @param progress - Animation progress (0 to 1)
 * @param direction - Animation direction ('entrance' or 'exit')
 * @returns CSS style object for fadeInDown animation
 */
export function calculateFadeInDownAnimation(progress: number, direction: 'entrance' | 'exit'): React.CSSProperties {
  // Validate input
  if (progress < 0 || progress > 1) {
    console.warn(`Invalid progress value: ${progress}. Must be between 0 and 1.`);
    progress = Math.max(0, Math.min(1, progress));
  }

  // For entrance direction, fade in and move down
  if (direction === 'entrance') {
    const opacity = interpolate(progress, [0, 1], [0, 1], {
      easing: Easing.out(Easing.ease)
    });
    const translateY = interpolate(progress, [0, 1], [-30, 0], {
      easing: Easing.out(Easing.ease)
    });
    
    return {
      opacity,
      transform: `translateY(${translateY}px) translateZ(0)`,
      willChange: 'opacity, transform'
    };
  } else {
    // For exit, reverse the animation (move up and fade out)
    const opacity = interpolate(progress, [0, 1], [1, 0], {
      easing: Easing.in(Easing.ease)
    });
    const translateY = interpolate(progress, [0, 1], [0, -30], {
      easing: Easing.in(Easing.ease)
    });
    
    return {
      opacity,
      transform: `translateY(${translateY}px) translateZ(0)`,
      willChange: 'opacity, transform'
    };
  }
}